import html2canvas from "html2canvas";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { animals } from "../data/animals";
import { useTestSession } from "../context/TestSessionContext";
import { calculateResult } from "../utils/calculateResult";

function ResultPage() {
  const navigate = useNavigate();
  const { answers, restart } = useTestSession();
  const cardRef = useRef(null);
  const [toast, setToast] = useState("");
  const [shareChannel, setShareChannel] = useState("xiaohongshu");
  const [previewImage, setPreviewImage] = useState("");

  const hasAnswer = answers.some(Boolean);

  const result = useMemo(() => {
    if (!hasAnswer) return null;
    return calculateResult(answers);
  }, [answers, hasAnswer]);

  if (!hasAnswer || !result) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <div className="rounded-xl bg-white p-6 text-center shadow-md">
          <p className="mb-4 text-gray-600">你还没有完成测试哦。</p>
          <button
            onClick={() => navigate("/")}
            className="rounded-lg bg-fox-orange px-4 py-2 font-semibold text-white"
          >
            去首页开始
          </button>
        </div>
      </main>
    );
  }

  const top = result.topAnimal;
  const matches = top.compatibility || [];
  const topThree = result.ranked.slice(0, 3);

  const handleRestart = () => {
    restart();
    navigate("/");
  };

  const showToast = (text) => {
    setToast(text);
    window.setTimeout(() => setToast(""), 1600);
  };

  const buildShareText = (channel) => {
    const base = window.location.origin;

    if (channel === "douyin") {
      return [
        `我居然测出了 ${top.emoji}${top.name}！`,
        `结果关键词：${top.strengths.slice(0, 2).join("、")}`,
        top.tagline,
        "你也来测一下，看看你是哪种动物人格：",
        base,
        "#心理测试 #性格测试 #动物人格",
      ].join("\n");
    }

    if (channel === "moments") {
      return [
        `我的测试结果：${top.emoji}${top.name}`,
        top.tagline,
        `最近在做自我认知，这个测试还挺有意思。`,
        `链接：${base}`,
      ].join("\n");
    }

    return [
      `测出来我是 ${top.emoji}${top.name}`,
      `真的有点准：${top.strengths.slice(0, 3).join("、")}`,
      top.tagline,
      "你也来试试这个动物人格测试：",
      base,
      "#趣味测试 #心理测试 #人格测试",
    ].join("\n");
  };

  const copyText = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const temp = document.createElement("textarea");
    temp.value = text;
    document.body.appendChild(temp);
    temp.select();
    document.execCommand("copy");
    document.body.removeChild(temp);
  };

  const handleCopyShareText = async () => {
    await copyText(buildShareText(shareChannel));
    showToast("分享文案已复制");
  };

  const handleDownloadPoster = async () => {
    if (!cardRef.current) return;
    const canvas = await html2canvas(cardRef.current, {
      scale: 2,
      backgroundColor: "#fffaf5",
    });
    const url = canvas.toDataURL("image/png");
    const isWeChat = /micromessenger/i.test(window.navigator.userAgent);

    if (isWeChat) {
      setPreviewImage(url);
      showToast("请长按图片保存到相册");
      return;
    }

    const link = document.createElement("a");
    link.href = url;
    link.download = `动物人格-${top.id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("海报已导出");
  };

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-8 text-gray-900 md:px-6">
      <section className="mx-auto max-w-4xl space-y-4">
        <div
          ref={cardRef}
          className="overflow-hidden rounded-3xl border border-orange-100 bg-[#fffaf5] shadow-xl"
        >
          <div className="bg-gradient-animal px-6 py-7 text-white md:px-8">
            <p className="mb-2 text-xs uppercase tracking-[0.18em] text-white/80">
              Animal Personality Report
            </p>
            <h1 className="text-3xl font-bold md:text-4xl">
              {top.emoji} {top.name}
            </h1>
            <p className="mt-2 text-sm text-white/90 md:text-base">{top.tagline}</p>
          </div>

          <div className="grid gap-4 p-6 md:grid-cols-2 md:p-8">
            <div className="rounded-2xl bg-white p-5 shadow-sm">
              <h2 className="mb-3 text-lg font-semibold">你的核心优势</h2>
              <ul className="space-y-2 text-gray-700">
                {top.strengths.map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full bg-fox-orange" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-white p-5 shadow-sm">
              <h2 className="mb-3 text-lg font-semibold">人格倾向 Top 3</h2>
              <div className="space-y-3">
                {topThree.map((item) => (
                  <div key={item.animalId}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">
                        {item.animal.emoji} {item.animal.name}
                      </span>
                      <span className="text-gray-500">{item.percent}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-orange-400 to-amber-300"
                        style={{ width: `${item.percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-5 text-orange-900 md:col-span-2">
              <h2 className="mb-2 text-lg font-semibold">成长建议</h2>
              <p>{top.tip}</p>
            </div>

            <div className="rounded-2xl bg-white p-5 shadow-sm md:col-span-2">
              <h2 className="mb-3 text-lg font-semibold">最佳匹配类型</h2>
              <div className="grid gap-3 md:grid-cols-2">
                {matches.map((match) => {
                  const peer = animals[match.id];
                  return (
                    <div
                      key={match.id}
                      className="rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm text-gray-700"
                    >
                      <p className="font-semibold text-gray-900">
                        {peer.emoji} {peer.name} · 匹配度 {match.score}
                      </p>
                      <p className="mt-1">{match.reason}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleRestart}
            className="rounded-xl bg-fox-orange px-5 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            再测一次
          </button>
          <button
            onClick={handleDownloadPoster}
            className="rounded-xl border border-gray-300 px-5 py-2.5 font-semibold text-gray-700 transition hover:bg-gray-100"
          >
            导出分享图
          </button>
          <button
            onClick={handleCopyShareText}
            className="rounded-xl border border-gray-300 px-5 py-2.5 font-semibold text-gray-700 transition hover:bg-gray-100"
          >
            复制分享文案
          </button>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="mb-3 text-sm font-semibold text-gray-700">选择分享平台文案</p>
          <div className="mb-3 flex flex-wrap gap-2">
            <button
              onClick={() => setShareChannel("xiaohongshu")}
              className={`rounded-lg px-3 py-1.5 text-sm ${
                shareChannel === "xiaohongshu"
                  ? "bg-fox-orange text-white"
                  : "border border-gray-300 text-gray-700"
              }`}
            >
              小红书
            </button>
            <button
              onClick={() => setShareChannel("douyin")}
              className={`rounded-lg px-3 py-1.5 text-sm ${
                shareChannel === "douyin"
                  ? "bg-fox-orange text-white"
                  : "border border-gray-300 text-gray-700"
              }`}
            >
              抖音
            </button>
            <button
              onClick={() => setShareChannel("moments")}
              className={`rounded-lg px-3 py-1.5 text-sm ${
                shareChannel === "moments"
                  ? "bg-fox-orange text-white"
                  : "border border-gray-300 text-gray-700"
              }`}
            >
              朋友圈
            </button>
          </div>
          <pre className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm leading-6 text-gray-700">
            {buildShareText(shareChannel)}
          </pre>
        </div>

        {previewImage ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="w-full max-w-sm rounded-2xl bg-white p-4 shadow-xl">
              <p className="mb-2 text-sm font-semibold text-gray-800">
                微信内请长按图片保存
              </p>
              <p className="mb-3 text-xs text-gray-500">
                保存后可在朋友圈或聊天中发送该海报。
              </p>
              <img
                src={previewImage}
                alt="测试分享海报"
                className="w-full rounded-lg border border-gray-200"
              />
              <button
                onClick={() => setPreviewImage("")}
                className="mt-3 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
              >
                关闭
              </button>
            </div>
          </div>
        ) : null}

        {toast ? (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-gray-900 px-4 py-2 text-sm text-white shadow-lg">
            {toast}
          </div>
        ) : null}
      </section>
    </main>
  );
}

export default ResultPage;
